// src/services/ServiceContainer.ts

export enum ServiceScope {
    Singleton = 'singleton',
    Transient = 'transient'
}

interface ServiceRegistration<T> {
    factory: () => T;
    scope: ServiceScope;
    instance?: T;
}

export class ServiceContainer {
    private static instance: ServiceContainer;
    private services: Map<new (...args: any[]) => any, ServiceRegistration<any>>;

    private constructor() {
        this.services = new Map();
    }

    static getInstance(): ServiceContainer {
        if (!ServiceContainer.instance) {
            ServiceContainer.instance = new ServiceContainer();
        }
        return ServiceContainer.instance;
    }

    register<T>(type: new (...args: any[]) => T, factory: () => T, scope: ServiceScope = ServiceScope.Singleton): void {
        this.services.set(type, { factory, scope });
    }

    get<T>(type: new (...args: any[]) => T): T {
        const registration = this.services.get(type);
        if (!registration) {
            throw new Error(`Service ${type.name} not registered`);
        }

        if (registration.scope === ServiceScope.Singleton) {
            if (!registration.instance) {
                registration.instance = registration.factory();
            }
            return registration.instance;
        } else {
            // For transient scope, create a new instance each time
            return registration.factory();
        }
    }

    // Helper method to register a singleton instance directly
    registerSingleton<T>(type: new (...args: any[]) => T, instance: T): void {
        this.services.set(type, {
            factory: () => instance,
            scope: ServiceScope.Singleton,
            instance
        });
    }

    // Helper method to register a transient factory
    registerTransient<T>(type: new (...args: any[]) => T, factory: () => T): void {
        this.services.set(type, {
            factory,
            scope: ServiceScope.Transient
        });
    }
}

/** Use dependency injection to get an instance of a class */
export function resolveType<T>(type: new (...args: any[]) => T): T {
    return ServiceContainer.getInstance().get(type);
}
